import { formatCurrency, planPriceAtMonth } from '@crm/shared';
import { Badge, Button, Card, CardContent, Checkbox, RadioGroup, Spinner } from '@crm/ui';
import {
  Check,
  CircleCheck,
  CircleDollarSign,
  CreditCard,
  FlaskConical,
  Layers,
  Pencil,
  Plus,
  Server,
  Settings,
  TriangleAlert,
  UserPlus,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmptyState, FormField, PageHeader, StatCard, TabPages, WIDGET_GRID } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import type { LicensePlan } from '@/modules/billing';
import {
  type AsaasEnvironment,
  type MessagePricingMode,
  type SaasSettings,
  type SaasSettingsInput,
  type TwilioMock,
  useSaasPlans,
  useSaasSettings,
  useSaveSaasSettings,
} from '../api';
import { PlanFormModal } from '../components/plan-form-modal';

const MODOS = [
  {
    value: 'AUTO' as const,
    label: 'Calculado pelo preco da Twilio',
    hint: 'O preco consultado na Twilio, convertido em real e multiplicado pela margem.',
  },
  {
    value: 'FIXED' as const,
    label: 'Preco fixo por mensagem',
    hint: 'O mesmo valor sempre, independente do que a Twilio cobrar.',
  },
];

const AMBIENTES = [
  {
    value: 'SANDBOX' as const,
    label: 'Sandbox',
    hint: 'Cobranca de mentira, para conferir o caminho sem ninguem pagar nada.',
  },
  {
    value: 'PRODUCTION' as const,
    label: 'Producao',
    hint: 'Cobranca de verdade: o assinante paga e o dinheiro entra.',
  },
];

const VAZIO: SaasSettingsInput = {
  messagePricingMode: 'AUTO',
  messagePrice: 0,
  messageMarginPercent: 100,
  usdRate: 5.5,
  lineMarginPercent: 10,
  minCreditTopUp: 50,
  asaasEnvironment: 'SANDBOX',
  asaasReturnUrl: '',
  waitlistEnabled: false,
  adminPollingMinutes: 5,
};

function Bloco({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="flush">
      <CardContent className="space-y-4 py-5">
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function Campo({
  label,
  hint,
  value,
  step,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  step: string;
  onChange: (valor: number) => void;
}) {
  return (
    <div className="space-y-1">
      <FormField
        label={label}
        name={label}
        type="number"
        min="0"
        step={step}
        value={String(value)}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function Escopo({ scope }: { scope: TwilioMock['scopes'][number] }) {
  return (
    <li className="flex flex-wrap items-start gap-3 py-3">
      <span
        className={
          scope.mocked
            ? 'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400'
            : 'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
        }
      >
        {scope.mocked ? <Server className="h-4 w-4" /> : <CircleCheck className="h-4 w-4" />}
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          {scope.label}
          <Badge variant="outline" className="font-mono text-xs">
            {scope.key}
          </Badge>
        </p>
        <p className="text-sm text-muted-foreground">
          {scope.mocked ? 'Respondido pelo WireMock' : 'Vai para a Twilio de verdade'}
        </p>
        <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
          {scope.covers.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function Simulador({ mock }: { mock: TwilioMock }) {
  const ligado = Boolean(mock.url);

  return (
    <Bloco
      title="Simulador da Twilio"
      description={
        ligado
          ? `O que estiver marcado abaixo fala com o WireMock em ${mock.url}, nao com a Twilio.`
          : 'Desligado: tudo fala com a Twilio de verdade.'
      }
    >
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Dominios
        </p>
        <ul className="flex flex-wrap gap-2">
          {mock.hosts.map((host) => (
            <li key={host.domain}>
              <Badge
                variant={host.mocked ? 'secondary' : 'outline'}
                className={
                  host.mocked ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' : undefined
                }
              >
                {host.domain} - {host.mocked ? 'simulado' : 'Twilio real'}
              </Badge>
            </li>
          ))}
        </ul>
      </div>

      <ul className="divide-y divide-border border-t border-border">
        {mock.scopes.map((scope) => (
          <Escopo key={scope.key} scope={scope} />
        ))}

        <li className="flex flex-wrap items-start gap-3 py-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <TriangleAlert className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium">{mock.neverMocked.label}</p>
            <p className="text-sm text-muted-foreground">
              Nunca e simulado, com o simulador ligado ou nao.
            </p>
            <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
              {mock.neverMocked.covers.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </li>
      </ul>

      <p className="text-xs text-muted-foreground">
        Isto se muda em TWILIO_MOCK_URL e TWILIO_MOCK_SCOPES, no ambiente da API - nao pela tela.
      </p>
    </Bloco>
  );
}

function Numeros({ dados, form }: { dados: SaasSettings; form: SaasSettingsInput }) {
  const automatico = form.messagePricingMode === 'AUTO';
  const previsto = automatico
    ? dados.providerPriceUsd === null
      ? null
      : dados.providerPriceUsd * form.usdRate * (1 + form.messageMarginPercent / 100)
    : form.messagePrice;

  return (
    <div className={WIDGET_GRID.three}>
      <StatCard
        title="Preco da Twilio"
        shortTitle="Twilio"
        value={
          dados.providerPriceUsd === null
            ? 'indisponivel'
            : `US$ ${dados.providerPriceUsd.toFixed(5)}`
        }
        hint="por mensagem, consultado na API"
      />
      <StatCard
        title="Cobrado hoje"
        shortTitle="Cobrado"
        value={
          dados.effectivePrice === null ? 'indisponivel' : formatCurrency(dados.effectivePrice)
        }
        hint="debitado a cada envio"
      />
      <StatCard
        title="Ficara em"
        value={previsto === null ? 'indisponivel' : formatCurrency(previsto)}
        hint="com o que esta na tela"
      />
    </div>
  );
}

/** A tabela dos planos que o salao pode contratar - so leitura aqui; quem
    cadastra e altera plano ainda e direto no banco. */
function TabelaPlanos() {
  const planos = useSaasPlans();
  const lista = planos.data?.data ?? [];
  // Nulo com o modal fechado; nulo com `editando=true` e "criar novo".
  const [selecionado, setSelecionado] = useState<LicensePlan | null>(null);
  const [criando, setCriando] = useState(false);

  if (planos.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCriando(true)}>
          <Plus className="h-4 w-4" />
          Novo plano
        </Button>
      </div>

      {lista.length === 0 ? (
        <EmptyState title="Nenhum plano cadastrado" />
      ) : (
        <Card variant="flush">
          <CardContent className="overflow-x-auto py-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-3 pr-4 font-medium">Plano</th>
                  <th className="py-3 pr-4 font-medium">Mensalidade</th>
                  <th className="py-3 pr-4 font-medium">Normal calculado</th>
                  <th className="py-3 pr-4 font-medium">Promocao inicial</th>
                  <th className="py-3 pr-4 font-medium">Unidades</th>
                  <th className="py-3 pr-4 font-medium">Usuarios</th>
                  <th className="py-3 pr-4 font-medium">Mensagens inclusas</th>
                  <th className="py-3 pr-4 font-medium">Status</th>
                  <th className="py-3 pr-4 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lista.map((plano) => (
                  <tr key={plano.id}>
                    <td className="py-3 pr-4">
                      <p className="font-medium">{plano.name}</p>
                      {plano.description ? (
                        <p className="text-xs text-muted-foreground">{plano.description}</p>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4">{formatCurrency(plano.monthlyPrice)}</td>
                    <td className="py-3 pr-4">
                      {plano.calculatedMonthlyPrice == null
                        ? 'Indisponivel'
                        : formatCurrency(plano.calculatedMonthlyPrice)}
                    </td>
                    <td className="py-3 pr-4">
                      {plano.introPrice != null && plano.introMonths > 0
                        ? `${formatCurrency(
                            planPriceAtMonth(plano, 1),
                          )} por ${plano.introMonths} meses`
                        : 'Sem promocao'}
                    </td>
                    <td className="py-3 pr-4">{plano.maxUnits ?? 'ilimitado'}</td>
                    <td className="py-3 pr-4">{plano.maxUsers ?? 'ilimitado'}</td>
                    <td className="py-3 pr-4">{plano.includedMessages ?? 'ilimitado'}</td>
                    <td className="py-3 pr-4">
                      <Badge variant={plano.isActive ? 'secondary' : 'outline'}>
                        {plano.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setSelecionado(plano)}>
                        <Pencil className="h-4 w-4" />
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        O normal calculado soma o aluguel mensal da linha Twilio e o pacote de mensagens incluso,
        aplicando as taxas configuradas na aba Precos.
      </p>

      {selecionado || criando ? (
        <PlanFormModal
          plano={selecionado}
          onClose={() => {
            setSelecionado(null);
            setCriando(false);
          }}
        />
      ) : null}
    </div>
  );
}

export function SaasSettingsPage() {
  useDocumentTitle('Configurações do Sistema');

  const settings = useSaasSettings();
  const salvar = useSaveSaasSettings();
  const [form, setForm] = useState<SaasSettingsInput>(VAZIO);
  const [apiKey, setApiKey] = useState('');
  const [webhookToken, setWebhookToken] = useState('');

  const dados = settings.data;

  useEffect(() => {
    if (!dados) return;

    setForm({
      messagePricingMode: dados.messagePricingMode,
      messagePrice: dados.messagePrice,
      messageMarginPercent: dados.messageMarginPercent,
      usdRate: dados.usdRate,
      lineMarginPercent: dados.lineMarginPercent,
      minCreditTopUp: dados.minCreditTopUp,
      asaasEnvironment: dados.asaasEnvironment,
      asaasReturnUrl: dados.asaasReturnUrl ?? '',
      waitlistEnabled: dados.waitlistEnabled,
      adminPollingMinutes: dados.adminPollingMinutes,
    });
  }, [dados]);

  if (settings.isLoading || !dados) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const automatico = form.messagePricingMode === 'AUTO';

  const enviar = () =>
    salvar.mutate({
      ...form,
      ...(apiKey ? { asaasApiKey: apiKey } : {}),
      ...(webhookToken ? { asaasWebhookToken: webhookToken } : {}),
    });

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<Settings />}
        title="Configurações do Sistema"
        description="Precos, cobranca, planos e como novos assinantes entram no sistema."
      />

      <TabPages
        label="Aba das configuracoes"
        pages={[
          {
            key: 'precos',
            label: 'Precos',
            icon: CircleDollarSign,
            render: () => (
              <div className="space-y-4">
                <Numeros dados={dados} form={form} />

                <Bloco
                  title="Preco da mensagem"
                  description="Quanto o assinante paga por mensagem enviada."
                >
                  <RadioGroup
                    label="Preco da mensagem"
                    options={MODOS}
                    value={form.messagePricingMode}
                    onChange={(messagePricingMode: MessagePricingMode) =>
                      setForm((atual) => ({ ...atual, messagePricingMode }))
                    }
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    {automatico ? (
                      <>
                        <Campo
                          label="Taxa por mensagem (%)"
                          hint="Quanto por cento acima do que a Twilio cobra por mensagem. 100 cobra o dobro."
                          step="1"
                          value={form.messageMarginPercent}
                          onChange={(messageMarginPercent) =>
                            setForm((atual) => ({ ...atual, messageMarginPercent }))
                          }
                        />
                        <Campo
                          label="Dolar"
                          hint="Quantos reais vale um dolar na conversao."
                          step="0.01"
                          value={form.usdRate}
                          onChange={(usdRate) => setForm((atual) => ({ ...atual, usdRate }))}
                        />
                      </>
                    ) : (
                      <Campo
                        label="Preco por mensagem"
                        hint="Em reais, debitado do saldo a cada envio."
                        step="0.0001"
                        value={form.messagePrice}
                        onChange={(messagePrice) =>
                          setForm((atual) => ({ ...atual, messagePrice }))
                        }
                      />
                    )}
                  </div>
                </Bloco>

                <Bloco
                  title="Linha e recarga"
                  description="O que o assinante paga pela linha e o piso de cada recarga avulsa."
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Campo
                      label="Taxa sobre a linha (%)"
                      hint="Quanto por cento acima do que a Twilio cobra pelo numero. 10 cobra 10% a mais."
                      step="1"
                      value={form.lineMarginPercent}
                      onChange={(lineMarginPercent) =>
                        setForm((atual) => ({ ...atual, lineMarginPercent }))
                      }
                    />
                    <Campo
                      label="Recarga minima (R$)"
                      hint="Abaixo disso o assinante nao consegue seguir no passo de creditos."
                      step="1"
                      value={form.minCreditTopUp}
                      onChange={(minCreditTopUp) =>
                        setForm((atual) => ({ ...atual, minCreditTopUp }))
                      }
                    />
                  </div>
                </Bloco>

                <Bloco
                  title="Atualizacao automatica do SaaS Admin"
                  description="Listas e relatorios se atualizam automaticamente neste intervalo."
                >
                  <Campo
                    label="Intervalo do polling (minutos)"
                    hint="Use de 1 a 1440 minutos. Configuracoes e cadastros nao fazem polling."
                    step="1"
                    value={form.adminPollingMinutes}
                    onChange={(adminPollingMinutes) =>
                      setForm((atual) => ({ ...atual, adminPollingMinutes }))
                    }
                  />
                </Bloco>

                <Card variant="flush">
                  <CardContent className="flex flex-wrap items-center gap-3 py-4">
                    <Button type="button" disabled={salvar.isPending} onClick={enviar}>
                      {salvar.isPending ? <Spinner /> : <Check className="h-4 w-4" />}
                      Salvar
                    </Button>

                    {salvar.isSuccess ? (
                      <span className="text-sm text-muted-foreground">Configuracoes salvas.</span>
                    ) : null}

                    {salvar.error ? (
                      <span className="text-sm text-destructive">
                        {toErrorMessage(salvar.error)}
                      </span>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            key: 'cobranca',
            label: 'Cobranca',
            icon: CreditCard,
            render: () => (
              <div className="space-y-4">
                <Bloco
                  title="Cobranca pelo Asaas"
                  description="E o Asaas que hospeda a pagina de pagamento; aqui ficam as credenciais dele."
                >
                  <RadioGroup
                    label="Ambiente"
                    options={AMBIENTES}
                    value={form.asaasEnvironment}
                    onChange={(asaasEnvironment: AsaasEnvironment) =>
                      setForm((atual) => ({ ...atual, asaasEnvironment }))
                    }
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <FormField
                        label="Chave de API"
                        name="asaas-api-key"
                        type="password"
                        autoComplete="off"
                        placeholder={
                          dados.asaasApiKeySet
                            ? `gravada, termina em ${dados.asaasApiKeyHint}`
                            : 'sem chave'
                        }
                        value={apiKey}
                        onChange={(event) => setApiKey(event.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        {dados.asaasApiKeyFromEnv
                          ? 'Hoje vale a chave do ambiente (ASAAS_API_KEY). O que for gravado aqui passa a valer no lugar dela.'
                          : 'Em branco mantem a chave que ja esta gravada.'}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <FormField
                        label="Token do webhook"
                        name="asaas-webhook-token"
                        type="password"
                        autoComplete="off"
                        placeholder={dados.asaasWebhookTokenSet ? 'gravado' : 'sem token'}
                        value={webhookToken}
                        onChange={(event) => setWebhookToken(event.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        O mesmo segredo cadastrado no Asaas. Sem ele o aviso de pagamento e
                        recusado.
                      </p>
                    </div>

                    <div className="space-y-1">
                      <FormField
                        label="URL de retorno"
                        name="asaas-return-url"
                        placeholder="https://app.seudominio.com/whatsapp"
                        value={form.asaasReturnUrl ?? ''}
                        onChange={(event) =>
                          setForm((atual) => ({ ...atual, asaasReturnUrl: event.target.value }))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Para onde o Asaas devolve o assinante depois de pagar.
                      </p>
                    </div>

                    <div className="space-y-1">
                      <FormField
                        label="URL do webhook"
                        name="asaas-webhook-url"
                        readOnly
                        value={dados.asaasWebhookUrl}
                      />
                      <p className="text-xs text-muted-foreground">
                        Cadastre este endereco no Asaas, em Integracoes.
                      </p>
                    </div>
                  </div>
                </Bloco>

                <Card variant="flush">
                  <CardContent className="flex flex-wrap items-center gap-3 py-4">
                    <Button type="button" disabled={salvar.isPending} onClick={enviar}>
                      {salvar.isPending ? <Spinner /> : <Check className="h-4 w-4" />}
                      Salvar
                    </Button>

                    {salvar.isSuccess ? (
                      <span className="text-sm text-muted-foreground">Configuracoes salvas.</span>
                    ) : null}

                    {salvar.error ? (
                      <span className="text-sm text-destructive">
                        {toErrorMessage(salvar.error)}
                      </span>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            ),
          },
          {
            key: 'simulador',
            label: 'Simulador',
            icon: FlaskConical,
            render: () => <Simulador mock={dados.twilioMock} />,
          },
          {
            key: 'planos',
            label: 'Planos',
            icon: Layers,
            render: () => <TabelaPlanos />,
          },
          {
            key: 'novas-assinaturas',
            label: 'Novas Assinaturas',
            icon: UserPlus,
            render: () => (
              <div className="space-y-4">
                <Bloco
                  title="Fila de espera"
                  description="Com a fila ligada, quem termina o cadastro nao cai direto no painel - fica esperando o time liberar o acesso."
                >
                  <Checkbox
                    label="Ativar fila de espera"
                    checked={form.waitlistEnabled}
                    onChange={(event) =>
                      setForm((atual) => ({ ...atual, waitlistEnabled: event.target.checked }))
                    }
                  />
                </Bloco>

                <Card variant="flush">
                  <CardContent className="flex flex-wrap items-center gap-3 py-4">
                    <Button type="button" disabled={salvar.isPending} onClick={enviar}>
                      {salvar.isPending ? <Spinner /> : <Check className="h-4 w-4" />}
                      Salvar
                    </Button>

                    {salvar.isSuccess ? (
                      <span className="text-sm text-muted-foreground">Configuracoes salvas.</span>
                    ) : null}

                    {salvar.error ? (
                      <span className="text-sm text-destructive">
                        {toErrorMessage(salvar.error)}
                      </span>
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
